import React from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Columns, GearSix, Gauge } from 'phosphor-react';
import { PageBox } from '../../components/pageBox';
import { useAuth } from '../../providers/auth';
import { useEventos, IEventoDetalhe } from '../../hooks/useEventos';
import { IDataNoPagination } from '../../types/shared';
import { KanbanBoardContent } from '../../components/kanban/KanbanBoardContent';
import { EventoArquivados } from './components/EventoArquivados';
import EventoDetalhe from './components/EventoDetalhe';
import EventoGerencial from './components/EventoGerencial';

const ABA = {
  fontSize: '13px',
  fontWeight: '500',
  px: 4,
  py: 2,
  borderRadius: 'full',
  color: 'gray.500',
  _selected: { bg: 'gray.900', color: 'white' },
  transition: 'all 0.15s ease',
};

/**
 * Pagina do evento (`/eventos/:eventoId`): cabecalho com os KPIs e as abas Gerencial e Kanban. A aba vai na URL
 * (`?aba=kanban`) para os links cairem direto no quadro; o acesso ao Kanban segue a tela `/eventos/kanban` do RBAC.
 */
export default function EventoPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { eventoId: param } = useParams();
  const eventoId = Number(param);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { getEvento } = useEventos();

  // a mesma chave do cabecalho (EventoDetalhe): uma busca so'
  const { data } = useQuery<IDataNoPagination<IEventoDetalhe>>({
    queryKey: ['evento-detalhe', eventoId],
    queryFn: getEvento,
    enabled: !!eventoId,
  });
  const evento = data?.data;

  const abas = hasPermission('/eventos/kanban') ? ['gerencial', 'kanban'] : ['gerencial'];
  const aba = abas.includes(searchParams.get('aba') ?? '')
    ? (searchParams.get('aba') as string)
    : 'gerencial';

  // `/eventos/<algo que nao e' id>`: sub-rota que a pessoa nao tem (ex.: /eventos/permissoes) cai aqui
  if (!Number.isInteger(eventoId) || eventoId <= 0) return <Navigate to="/eventos" replace />;

  const trocarAba = (i: number) => {
    // o quadro mexe em contrato e status: o cabecalho e a Gerencial voltam com o dado do servidor
    queryClient.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
    queryClient.invalidateQueries({ queryKey: ['evento-contratos', eventoId] });
    setSearchParams(abas[i] === 'gerencial' ? {} : { aba: abas[i] }, { replace: true });
  };

  return (
    <PageBox>
      <Box bg="gray.50" minH="100vh" px={{ base: 4, md: 6 }} py={4}>
        <EventoDetalhe eventoId={eventoId} onVoltar={() => navigate('/eventos')} />

        {evento && (
          <Tabs
            variant="unstyled"
            isLazy
            lazyBehavior="keepMounted"
            index={abas.indexOf(aba)}
            onChange={trocarAba}
          >
            <Flex align="center" justify="space-between" mb={4} gap={2} flexWrap="wrap">
              <TabList gap={1}>
                <Tab {...ABA}>
                  <Gauge size={14} style={{ marginRight: 6 }} />
                  Gerencial
                </Tab>
                {abas.includes('kanban') && (
                  <Tab {...ABA}>
                    <Columns size={14} style={{ marginRight: 6 }} />
                    Kanban
                  </Tab>
                )}
              </TabList>
              {aba === 'kanban' && (
                <HStack spacing={1}>
                  {evento.kanban_projeto_id && <EventoArquivados eventoId={evento.id} />}
                  {hasPermission('/configuracoes/permissoes') && (
                    <Tooltip label="Permissões" fontSize="xs">
                      <Button
                        size="sm"
                        variant="ghost"
                        leftIcon={<GearSix size={15} weight="bold" />}
                        borderRadius="full"
                        color="gray.500"
                        fontSize="13px"
                        fontWeight="500"
                        onClick={() => navigate('/configuracoes/permissoes?aba=kanban')}
                      >
                        Permissões
                      </Button>
                    </Tooltip>
                  )}
                </HStack>
              )}
            </Flex>
            <TabPanels>
              <TabPanel px={0} py={0}>
                <EventoGerencial evento={evento} />
              </TabPanel>
              {abas.includes('kanban') && (
                <TabPanel px={0} py={0}>
                  {evento.kanban_projeto_id ? (
                    <KanbanBoardContent
                      projetoId={evento.kanban_projeto_id}
                      forceEditable
                      hideHeader
                    />
                  ) : (
                    <Flex justify="center" align="center" minH="300px">
                      <Text color="gray.400" fontSize="sm">
                        Este evento ainda não tem um Kanban vinculado.
                      </Text>
                    </Flex>
                  )}
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        )}
      </Box>
    </PageBox>
  );
}
